<script setup lang="ts">
import type { NuxtError } from "#app";

const props = defineProps<{
    error: NuxtError;
}>();

const auth = useAuth();

const config = useAppConfig();

const { t, te } = useI18n();

const jsonMessage = computed(() => {
    try {
        return props.error?.message && typeof props.error?.message === "string"
            ? JSON.parse(props.error?.message)
            : undefined;
    } catch (e) {
        return undefined;
    }
});

const errorCode = computed(() => {
    const code = props.error?.statusCode ?? "500";
    if (
        jsonMessage.value &&
        (jsonMessage.value.status ||
            jsonMessage.value.code ||
            jsonMessage.value.statusCode ||
            jsonMessage.value.errorCode)
    ) {
        return (
            jsonMessage.value.status ??
            jsonMessage.value.code ??
            jsonMessage.value.statusCode ??
            jsonMessage.value.errorCode ??
            code
        );
    }

    return code;
});

const errorMessage = computed(() => {
    const msg = props.error?.statusMessage ?? "Server Error";

    if (jsonMessage.value && jsonMessage.value.message) {
        return jsonMessage.value?.message ?? msg;
    }

    return msg;
});

const errorDescription = computed(() => {
    const key = "errors." + errorCode.value;
    const description = te(key) ? t(key) : undefined;

    if (
        jsonMessage.value &&
        (jsonMessage.value.response ||
            jsonMessage.value.code ||
            jsonMessage.value.statusCode ||
            jsonMessage.value.errorCode)
    ) {
        return jsonMessage.value?.response ?? description;
    }

    return description;
});

const title = `${errorCode.value}: ${errorMessage.value} - ${config.title}`;

useServerSeoMeta({
    title: title,
    description: errorDescription.value,
    ogUrl: useRoute().fullPath,
    ogType: "website",
    ogTitle: title,
    ogDescription: errorDescription.value,
    twitterCard: "summary_large_image",
    twitterImage: {
        url: "/thumbnail.png",
        alt: title,
    },
    twitterTitle: title,
    twitterDescription: errorDescription.value,
});

useSeoMeta({
    title: title,
    description: errorDescription.value,
    ogUrl: useRoute().fullPath,
    ogType: "website",
    ogTitle: title,
    ogDescription: errorDescription.value,
    twitterCard: "summary_large_image",
    twitterImage: {
        url: "/thumbnail.png",
        alt: title,
    },
    twitterTitle: title,
    twitterDescription: errorDescription.value,
});
</script>

<template>
    <div class="relative w-screen h-screen overflow-hidden flex">
        <AppBuilderPage
            v-if="auth?.isAuthenticated && !useRoute().meta?.sanctum?.excluded"
            :title="errorMessage"
            :breadcrumbs="[{ to: '/' }]"
            :label="errorCode?.toString()"
        >
            <main
                class="w-full h-full bg-hd-neutral-50 rounded-xl border border-gray-300"
            >
                <UContainer
                    class="text-center"
                    :ui="{ padding: 'px-6 py-32 sm:py-40 lg:px-8' }"
                >
                    <p class="text-base font-semibold leading-8 text-primary">
                        {{ errorCode }}
                    </p>
                    <h1
                        class="mt-4 text-3xl font-bold tracking-tight text-hd-primary-original sm:text-5xl"
                    >
                        {{ errorMessage }}
                    </h1>
                    <p
                        v-if="errorDescription"
                        class="mt-4 text-base text-hd-primary-original/70 sm:mt-6"
                    >
                        {{ errorDescription }}
                    </p>
                </UContainer>
            </main>
        </AppBuilderPage>
        <main
            v-else
            class="w-full h-full bg-hd-neutral-50 rounded-xl border border-gray-300"
        >
            <UContainer
                class="text-center"
                :ui="{ padding: 'px-6 py-32 sm:py-40 lg:px-8' }"
            >
                <p class="text-base font-semibold leading-8 text-primary">
                    {{ errorCode }}
                </p>
                <h1
                    class="mt-4 text-3xl font-bold tracking-tight text-hd-primary-original sm:text-5xl"
                >
                    {{ errorMessage }}
                </h1>
                <p
                    v-if="errorDescription"
                    class="mt-4 text-base text-hd-primary-original/70 sm:mt-6"
                >
                    {{ errorDescription }}
                </p>
            </UContainer>
        </main>
    </div>
</template>
